import React from 'react';
import { useTheme } from '@mui/material/styles';
import cx from 'classnames';
import Typography from '@mui/material/Typography';
import Select, { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import FormLabel from './FormLabel';

export const compactSelectStyles = {
  '& .react-select > div[class*="control"]': {
    'minHeight': 'auto',
    '& > div:nth-child(1)': {
      fontSize: 13,
      py: 0,
      px: 1,
      pr: 0,
    },
    '& div[class*="indicatorContainer"]': {
      'padding': 0,
      'paddingRight': 0.6,
      '& > svg': {
        width: 15,
      },
    },
  },
  '& div[class*="placeholder"]': {
    color: 'text.primary',
    opacity: 1,
    fontWeight: 500,
  },
};

const MultiValue = (props) => {
  const maxToShow = props.selectProps.maxToShow || 3;

  if (props.index > maxToShow) return null;
  else if (props.index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  const length = props.getValue().length;
  const shouldBadgeShow = length > maxToShow;
  const displayLength = length - maxToShow;

  if (shouldBadgeShow) {
    const { children, ...rest } = props;
    return (
      <components.MultiValueLabel {...rest}>
        <Typography variant="caption">{`+${displayLength} more`}</Typography>
      </components.MultiValueLabel>
    );
  }
  return <components.MultiValue {...props} />;
};

const getTheme = (muiTheme) => (theme) => {
  return {
    ...theme,
    borderRadius: 12,
    spacing: {
      baseUnit: 4,
      controlHeight: 40,
      menuGutter: 8,
    },
    colors: {
      ...theme.colors,
      danger: muiTheme.palette.error.main,
      neutral0: muiTheme.palette.background.paper,
      neutral5: muiTheme.palette.action.disabledBackground,
      neutral10: muiTheme.palette.grey['100'],
      primary: muiTheme.palette.text.primary,
      primary25: muiTheme.palette.action.hover,
      neutral30: muiTheme.palette.grey['300'],
      neutral20: muiTheme.palette.grey['200'],
      neutral80: muiTheme.palette.text.primary,
      selected: muiTheme.palette.action.selected,
      focusColor: muiTheme.palette.primary.lighter,
      activeColor: muiTheme.palette.primary.light,
    },
  };
};

const customStyles = {
  container: (provided) => ({
    ...provided,
    'label + &': {
      marginTop: 4,
    },
  }),
  control: (provided, { theme, selectProps }) => {
    const isOpen = selectProps.menuIsOpen;
    return {
      ...provided,
      'minHeight': selectProps.size === 'small' ? 'auto' : 40,
      'borderRadius': 12,
      'boxShadow': null,
      'borderColor': selectProps.hasError
        ? theme.colors.danger
        : isOpen
        ? theme.colors.activeColor
        : theme.colors.neutral30,
      '&:hover': {
        borderColor: selectProps.hasError
          ? theme.colors.danger
          : isOpen
          ? theme.colors.activeColor
          : theme.colors.focusColor,
      },
    };
  },
  valueContainer: (provided) => ({
    ...provided,
    padding: '4px 16px',
    fontSize: 14,
  }),
  placeholder: (provided, { theme }) => ({
    ...provided,
    color: theme.colors.primary,
    fontWeight: 400,
    opacity: 0.42,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    transition: 'all .2s ease',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    color: state.theme.colors.primary,
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: 0,
  }),
  menu: (provided, { theme }) => ({
    ...provided,
    zIndex: 99,
    overflow: 'hidden',
    width: 'max-content',
    minWidth: '100%',
    maxWidth: 400,
  }),
  option: (provided, { isSelected, isDisabled, theme }) => ({
    ...provided,
    backgroundColor: isSelected ? theme.colors.selected : null,
    color: isDisabled ? theme.colors.neutral20 : theme.colors.primary,
    fontSize: 14,
  }),
  singleValue: (provided, { isDisabled, theme }) => ({
    ...provided,
    color: 'inherit',
    fontWeight: 400,
    opacity: isDisabled ? 0.42 : 1,
  }),
};

const CustomSelect = React.forwardRef(
  (
    {
      className,
      placeholder = 'Please select',
      label,
      tooltip,
      hint,
      options,
      value,
      onChange,
      hasError,
      helpText,
      loadOptions,
      paginate,
      transformValue,
      isMulti = false,
      cacheOptions = true,
      size,
      styles,
      sx,
      fullWidth,
      margin = 'normal',
      disabled,
      CustomMenu,
      ...rest
    },
    ref,
  ) => {
    const muiTheme = useTheme();

    const AsyncComponent = paginate ? AsyncPaginate : AsyncSelect;

    const handleChange = (value, action) => {
      if (transformValue) {
        if (isMulti) {
          onChange(
            value.map((v) => v.value),
            action,
          );
        } else {
          onChange(value?.value, action);
        }
      } else {
        onChange(value, action);
      }
    };

    const getValue = () => {
      if (transformValue && options) {
        if (isMulti) {
          return (options || []).filter((o) => (value || []).includes(o.value));
        }
        let option = null;
        options.find((i) => {
          if (i.options) {
            const isMatch = i.options.find((i) => i.value === value);
            if (isMatch) {
              option = isMatch;
            }
            return isMatch;
          }
          const isMatch = i.value === value;
          if (isMatch) {
            option = i;
          }
          return isMatch;
        });
        return option;
      } else {
        return value;
      }
    };

    const Components = {
      IndicatorSeparator: null,
    };
    if (rest.maxToShow) {
      Components.MultiValue = MultiValue;
    }
    if (CustomMenu) {
      Components.MenuList = CustomMenu;
    }

    return (
      <FormControl
        size={size}
        variant="standard"
        margin={margin}
        className={className}
        sx={
          size === 'compact' ? Object.assign({}, sx, compactSelectStyles) : sx
        }
        fullWidth={fullWidth}
        error={hasError}
      >
        {label && (
          <FormLabel label={label} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        {loadOptions ? (
          <AsyncComponent
            ref={ref}
            size={size}
            isDisabled={disabled}
            theme={getTheme(muiTheme)}
            className={cx('react-select', size)}
            components={Components}
            cacheOptions={cacheOptions}
            loadOptions={loadOptions}
            defaultOptions={options}
            placeholder={placeholder}
            onChange={handleChange}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            styles={customStyles}
            value={getValue()}
            hasError={hasError}
            hideSelectedOptions={!Components.MultiValue}
            {...rest}
          />
        ) : (
          <Select
            ref={ref}
            size={size}
            hasError={hasError}
            theme={getTheme(muiTheme)}
            className={cx('react-select', size)}
            components={Components}
            placeholder={placeholder}
            options={options}
            value={getValue()}
            onChange={handleChange}
            isDisabled={disabled}
            isMulti={isMulti}
            hideSelectedOptions={!Components.MultiValue}
            closeMenuOnSelect={!isMulti}
            styles={styles ? styles : customStyles}
            {...rest}
          />
        )}
        {hint && (
          <Typography
            component="p"
            variant="caption"
            color="textSecondary"
            sx={{ pl: 2, pt: 0.5 }}
          >
            {hint}
          </Typography>
        )}
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  },
);

CustomSelect.propTypes = {
  placeholder: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.any }),
  ),
  value: PropTypes.any,
  onChange: PropTypes.func,
  isMulti: PropTypes.bool,
  transformValue: PropTypes.bool,
};

export default CustomSelect;
